/**
 * ClientConnect per-client custom fields ("Additional Details").
 *
 * Values live in ClientPortalCustomFields.Identifier as a JSON map keyed by the contact's
 * display name, and that storage stays as it is. These helpers keep a contact's values
 * reachable through a name change:
 *
 * - On a screen, a contact's values are read and written under a key fixed when the page
 *   loaded (its ContactIdentity), never under the name being typed. Typing a new name
 *   therefore neither hides the values nor picks up values another contact left under
 *   that name.
 * - On submit, the value is written under every name the contact is known by: the name on
 *   the file, a pending name the client submitted earlier, and the name typed now. The
 *   lawyer reads the pending name until Import to File and the file name again if the
 *   rename is rejected, so both carry the latest value.
 * - When a rename reaches the server, the contact's own value is copied under the new name.
 * - Keys are never removed.
 *
 * Shared by ClientPortal (client), ReactShell (lawyer) and ClientPortalServer.
 */

export type IdentifierMap = Record<string, unknown>;

export type Rename = { from: string; to: string };

export type ContactLike = {
    buyerName?: string | null;
    sellerName?: string | null;
    clientName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
} | null | undefined;

export const CONTACT_ARRAYS = [ 'buyers', 'sellers', 'clients' ] as const;

export type ContactArray = typeof CONTACT_ARRAYS[number];

export type FileContacts = Partial<Record<ContactArray, unknown>> | null | undefined;

/** The key a contact's values are stored under, plus the other names it is known by. */
export type ContactIdentity = { key: string; names: string[] };

const NEW_CONTACT_PREFIX = '#new-contact:';

/** Key for a contact added on the current screen; it has no name yet and is never submitted. */
export const newContactKey = (serial: number): string => `${ NEW_CONTACT_PREFIX }${ serial }`;

export const isNewContactKey = (key: string): boolean => key.startsWith(NEW_CONTACT_PREFIX);

/** Same formula the contact cards use. MB/BC/SK files carry an empty buyerName, so the name
 *  falls through to "Last, First" there. */
export const contactDisplayName = (contact: ContactLike): string => {
    if (!contact) {
        return '';
    }
    if (contact.buyerName) {
        return contact.buyerName;
    }
    if (contact.sellerName) {
        return contact.sellerName;
    }
    if (contact.clientName) {
        return contact.clientName;
    }
    if (contact.firstName && contact.lastName) {
        return `${ contact.lastName }, ${ contact.firstName }`;
    }
    if (contact.firstName) {
        return contact.firstName;
    }
    return contact.lastName ?? '';
};

const contactsOf = (file: FileContacts, array: ContactArray): ContactLike[] => {
    const contacts = file?.[array];
    return Array.isArray(contacts) ? contacts as ContactLike[] : [];
};

/** Display name of every contact on a file, by array and position. */
export const fileContactNames = (file: FileContacts): Record<ContactArray, string[]> => ({
    buyers: contactsOf(file, 'buyers').map(contactDisplayName),
    sellers: contactsOf(file, 'sellers').map(contactDisplayName),
    clients: contactsOf(file, 'clients').map(contactDisplayName)
});

export const parseIdentifierMap = (identifier: unknown): IdentifierMap | null => {
    if (typeof identifier !== 'string' || identifier === '') {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(identifier);
        return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as IdentifierMap
            : null;
    } catch {
        return null;
    }
};

const valueAt = (map: IdentifierMap, key: string): string | undefined => {
    const raw = map[key];
    if (typeof raw === 'string') {
        return raw;
    }
    if (typeof raw === 'number') {
        return String(raw);
    }
    return undefined;
};

// The key first; the other names only cover data written before this logic existed, when a
// pending rename left the value under the file name alone.
const lookup = (map: IdentifierMap, identity: ContactIdentity): string | undefined => {
    for (const key of [ identity.key, ...identity.names ]) {
        const value = valueAt(map, key);
        if (value !== undefined) {
            return value;
        }
    }
    return undefined;
};

/** The value a contact sees. Empty when it has none, including when the name typed now
 *  matches a key left behind by another contact. */
export const readPerClientValue = (identifier: unknown, identity: ContactIdentity | undefined): string => {
    const map = parseIdentifierMap(identifier);
    if (!map || !identity) {
        return '';
    }
    return lookup(map, identity) ?? '';
};

export const writePerClientValue = (identifier: unknown, identity: ContactIdentity, value: string): string => {
    const map = parseIdentifierMap(identifier) ?? {};
    return JSON.stringify({ ...map, [identity.key]: value });
};

/** Every name a submitted value is written under. */
export const submitNames = (identity: ContactIdentity, currentName: string): string[] => {
    const names: string[] = [];
    for (const name of [ identity.key, ...identity.names, currentName ]) {
        if (name && !isNewContactKey(name) && !names.includes(name)) {
            names.push(name);
        }
    }
    return names;
};

export type SubmittingContact = { identity: ContactIdentity; currentName: string };

/** The map the client submits for one field: each contact's value under all of its names,
 *  with the keys of contacts added on this screen replaced by their typed name. Returns
 *  null when the Identifier is not a map. */
export const prepareIdentifierForSubmit = (identifier: unknown, contacts: SubmittingContact[]): string | null => {
    const map = parseIdentifierMap(identifier);
    if (!map) {
        return null;
    }
    const next: IdentifierMap = { ...map };
    for (const contact of contacts) {
        const value = lookup(map, contact.identity);
        if (value === undefined) {
            continue;
        }
        for (const name of submitNames(contact.identity, contact.currentName)) {
            next[name] = value;
        }
    }
    const submitted: IdentifierMap = {};
    for (const [ key, value ] of Object.entries(next)) {
        if (!isNewContactKey(key)) {
            submitted[key] = value;
        }
    }
    return JSON.stringify(submitted);
};

/** Server, on a client submit: the submitted entries are laid over the stored map, so keys
 *  the page never loaded (copies made for a rename, values another contact submitted) are
 *  kept. A submitted empty string still clears the value. */
export const mergeIdentifierMaps = (stored: unknown, submitted: unknown): unknown => {
    const storedMap = parseIdentifierMap(stored);
    const submittedMap = parseIdentifierMap(submitted);
    if (!storedMap) {
        return submitted;
    }
    if (!submittedMap) {
        return stored;
    }
    return JSON.stringify({ ...storedMap, ...submittedMap });
};

/** Server, when a contact is renamed: its own value is copied under the new name, replacing
 *  anything a deleted contact left there. The old key stays. Returns null when nothing
 *  changes. */
export const copyRenamedValues = (identifier: unknown, renames: Rename[]): string | null => {
    const map = parseIdentifierMap(identifier);
    if (!map) {
        return null;
    }
    const next: IdentifierMap = { ...map };
    let changed = false;
    for (const rename of renames) {
        if (!rename.from || !rename.to || rename.from === rename.to) {
            continue;
        }
        if (map[rename.from] === undefined && map[rename.to] === undefined) {
            continue;
        }
        const value = valueAt(map, rename.from) ?? '';
        if (next[rename.to] !== value) {
            next[rename.to] = value;
            changed = true;
        }
    }
    return changed ? JSON.stringify(next) : null;
};

/** Contacts whose display name differs between the stored file and an update, paired by
 *  position. A partial update (a selective merge carries only the accepted fields) is laid
 *  over the stored contact first, so a rejected name is not a rename. */
export const contactRenames = (stored: FileContacts, update: FileContacts): Rename[] => {
    const renames: Rename[] = [];
    for (const array of CONTACT_ARRAYS) {
        const before = contactsOf(stored, array);
        const after = contactsOf(update, array);
        before.forEach((contact, index) => {
            const change = after[index];
            if (!contact || !change) {
                return;
            }
            const from = contactDisplayName(contact);
            const to = contactDisplayName({ ...contact, ...change });
            if (from && to && from !== to) {
                renames.push({ from, to });
            }
        });
    }
    return renames;
};
